import {noop} from '@benchling/js-utils';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {showFlowchartEditFunctionality} from 'analysis-tool/EditAnalysisHelpers';
import {BulkArchiveFilesOptions} from 'archive/ArchiveActions';
import {getArchiveAction, getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {MoveToTemplateCollectionModal} from 'notebook/entry-templates/collections/MoveToTemplateCollectionModal';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import Badge, {BadgeColor} from 'ui-library/Badge';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';
import removeNulls from 'util/removeNulls';

import {
  copyAnalysisTemplate,
  moveAnalysisTemplateCollection,
  renameAnalysisTemplate,
} from './AnalysisTemplateActions';
import AnalysisTemplateNameEditor from './AnalysisTemplateNameEditor';
import {AnalysisTemplateGraphQL} from './AnalysisTemplateTypes';
import DuplicateAnalysisTemplateModal, {
  DuplicateAnalysisTemplateModalProps,
} from './DuplicateAnalysisTemplateModal';
import {refreshAnalysisTemplateQuery} from './graphql/GetAnalysisTemplate';
import PublishAnalysisTemplateDropdown from './PublishAnalysisTemplateDropdown';

const READ_ONLY_TOOLTIP_TEXT = 'For edit access please contact the project owner.';

export interface AnalysisTemplateHeaderProps {
  analysisTemplate: AnalysisTemplateGraphQL;
}

export default function AnalysisTemplateHeader({analysisTemplate}: AnalysisTemplateHeaderProps): JSX.Element {
  function handleUnarchive(): void {
    getUnarchiveAction([analysisTemplate.id])([analysisTemplate.id], undefined, () => {
      refreshAnalysisTemplateQuery(analysisTemplate.id);
    });
  }

  async function handleMove(newTemplateCollectionId: string): Promise<void> {
    await withLoadingScreen(moveAnalysisTemplateCollection(analysisTemplate, newTemplateCollectionId));
  }

  async function handleRename(newName: string): Promise<void> {
    await renameAnalysisTemplate(analysisTemplate, newName);
  }

  const isReadOnly = !(
    analysisTemplate.permissions.writable ||
    analysisTemplate.permissions.unarchivable ||
    analysisTemplate.permissions.archivable
  );

  const overflowMenuOptions: Array<OverflowMenuOption> = removeNulls([
    {
      icon: 'audit-trail',
      contents: <span>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</span>,
      onSelect: () => {
        exportAuditLogs([analysisTemplate.id]);
      },
    },
    BenchlingConfig.ENABLE_ANALYSIS_TOOL_EDITABLE_TEMPLATES
      ? {
          icon: 'copy',
          contents: <span>Duplicate</span>,
          onSelect: () => {
            runModal<DuplicateAnalysisTemplateModalProps>(DuplicateAnalysisTemplateModal, {
              sourceAnalysisTemplateName: analysisTemplate.name,
              sourceAnalysisTemplateCollectionId: analysisTemplate.templateCollection.id,
              onRequestClose: noop,
              onRequestSubmit: async (params) => {
                await withLoadingScreen(
                  copyAnalysisTemplate({
                    name: params.name,
                    copiedFromAnalysisTemplateId: analysisTemplate.id,
                    templateCollectionId: params.templateCollectionId,
                  })
                );
              },
            });
          },
        }
      : null,
    analysisTemplate.permissions.archivable && !isArchived(analysisTemplate)
      ? {
          icon: 'archive',
          contents: <span>Archive analysis template</span>,
          onSelect: () => {
            ArchiveModal.open({
              items: [analysisTemplate],
              onArchive: (
                templateIds: Array<string>,
                purpose: string,
                options: BulkArchiveFilesOptions
              ): void => {
                getArchiveAction(templateIds)(templateIds, purpose, options, () => {
                  refreshAnalysisTemplateQuery(analysisTemplate.id);
                });
              },
            });
          },
        }
      : null,
    analysisTemplate.permissions.unarchivable && isArchived(analysisTemplate)
      ? {
          icon: 'unarchive',
          contents: <span>Unarchive analysis template</span>,
          onSelect: handleUnarchive,
        }
      : null,
    analysisTemplate.permissions.writable && analysisTemplate.permissions.movable
      ? {
          icon: 'move',
          contents: <span>Move</span>,
          onSelect: () => {
            runModal(MoveToTemplateCollectionModal, {
              title: 'Move analysis template',
              onRequestSubmit: handleMove,
              onRequestClose: noop,
              initialTemplateCollectionId: analysisTemplate.templateCollection.id,
            });
          },
        }
      : null,
  ]);

  return (
    <div className={css(styles.headerBar)}>
      {isArchived(analysisTemplate) && (
        <div className={css(styles.bannerWrapper)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            icon="box-archive"
            text={`This analysis template is archived (${analysisTemplate.archiveRecord.purpose}).`}
            dataTestAttrs={{component: 'AnalysisTemplateHeader', element: 'archiveBanner'}}
          />
        </div>
      )}
      <div className={css(styles.contentWrapper)}>
        <div className="u-lr-split">
          <AnalysisTemplateNameEditor
            name={analysisTemplate.name}
            onRename={handleRename}
            disabled={!analysisTemplate.permissions.writable || isArchived(analysisTemplate)}
          />
          {BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE && (
            <Badge startingIcon={ModelTypeConstants.TEMPLATE_COLLECTION} color={BadgeColor.COLLECTIBLE_BLUE}>
              Template
            </Badge>
          )}
        </div>
        <div className={css(styles.rightSide)}>
          {isReadOnly && BenchlingConfig.ENABLE_ANALYSIS_TOOL_EDITABLE_TEMPLATES && (
            <Tooltip text={READ_ONLY_TOOLTIP_TEXT}>
              <span className={css(styles.readOnly)}>Read-only</span>
            </Tooltip>
          )}
          {showFlowchartEditFunctionality(analysisTemplate) &&
            BenchlingConfig.ENABLE_ANALYSIS_TOOL_EDITABLE_TEMPLATES && (
              <PublishAnalysisTemplateDropdown analysisTemplate={analysisTemplate} />
            )}
          <OverflowMenu
            buttonProps={{
              size: ButtonSize.SMALL,
              buttonStyle: ButtonStyle.TERTIARY_GRAY,
              dataTestAttrs: {component: 'AnalysisTemplateHeader', element: 'overflowMenu'},
            }}
            enableTooltip={true}
            menuProps={{dropLeft: true}}
            options={overflowMenuOptions}
            tooltipPlacement={FloatingPlacement.LEFT}
          />
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  headerBar: {
    marginBottom: 4,
    boxShadow: `0px 2px 4px ${GRAY_LIGHT}`,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  bannerWrapper: {
    marginTop: 15,
    marginLeft: 15,
    marginRight: 15,
  },
  contentWrapper: {
    margin: 15,
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-between',
  },
  readOnly: {
    ...FONT_PROXIMA_TITLE,
    color: GRAY_DARK,
  },
  rightSide: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
});
